// 证件模块的开发日志适配层：统一事件名和字段名，生产环境完全关闭。
const DEBUG_ENABLED = process.env.NODE_ENV !== "production";
const PREFIX = "[KYC 证件检测]";

const EVENT_LABELS = {
  "Camera state changed": "摄像头状态变化",
  "Camera stream stopped": "摄像头画面已停止",
  "Capture pipeline stopped": "采集流程已停止",
  "Preparing captured document preview": "正在生成证件预览",
  "Document capture completed": "证件采集完成",
  "Preview extraction failed": "证件预览生成失败",
  "Document perspective crop started": "正在按证件四角裁切预览图",
  "Document perspective crop completed": "证件预览图透视裁切完成",
  "Document perspective crop failed": "证件预览图裁切失败，使用原始画面",
  "Background capture started": "后台采集已开始",
  "Background capture did not start": "后台采集启动失败",
  "Capture triggered": "已触发拍摄",
  "Automatic capture conditions met": "稳定和最短采集时间均已满足",
  "Frame capture failed": "当前画面截取失败",
  "Component mounted": "证件相机组件已加载",
  "Component unmounted": "证件相机组件已卸载",
  "Automatic frame inspection started": "自动证件检测已开始",
  "Detection result": "证件检测结果",
  "Detector frame failed": "证件检测单帧处理失败，暂时保留跟踪结果",
  "Document detector unavailable": "证件检测器不可用，已切换手动模式",
  "Automatic frame inspection stopped": "自动证件检测已停止",
  "Capture mode changed": "拍摄模式已切换",
  "Requesting camera": "正在请求摄像头权限",
  "Camera stream active": "摄像头画面已开启",
  "Automatic detection enabled": "自动证件检测已启用",
  "Camera request failed": "摄像头请求失败",
  "Detection worker crashed": "证件检测线程异常退出",
  "Waiting for detector worker initialization": "正在等待证件检测线程初始化",
  "Starting Scanic detection worker": "正在启动 Scanic 证件检测线程",
  "Detector worker initialization timed out": "证件检测线程初始化超时",
  "Detector worker initialization failed": "证件检测线程初始化失败",
  "Scanic detector worker ready": "Scanic 证件检测线程已就绪",
  "Stopping detector worker": "正在停止证件检测线程",
  "Detector is ready after mode change": "模式切换后证件检测器已就绪",
  "Detector initialization confirmed": "证件检测器初始化成功",
  "Video frames are entering detector": "视频帧已送入证件检测器",
};

const DETAIL_LABELS = {
  side: "证件面",
  autoStart: "自动启动",
  from: "原状态",
  to: "新状态",
  mode: "拍摄模式",
  trigger: "触发方式",
  intervalMs: "检测间隔毫秒",
  sampleWidth: "检测采样宽度",
  sampleHeight: "检测采样高度",
  stableDurationMs: "稳定阈值毫秒",
  targetDetectorFps: "目标检测帧率",
  initializationMs: "初始化耗时毫秒",
  bufferWindowMs: "录像缓冲毫秒",
  videoBytes: "视频字节数",
  previewBytes: "预览图字节数",
  hasCapturedFrame: "已有截帧",
  durationMs: "录制时长毫秒",
  mimeType: "媒体类型",
  frame: "检测帧序号",
  status: "检测状态",
  documentDetected: "检测到证件",
  detected: "检测到证件",
  corners: "四角数量",
  detectorCorners: "Detector 原始四角",
  normalizedCorners: "归一化四角",
  complete: "证件完整",
  centered: "位置居中",
  sizeValid: "尺寸合格",
  positionValid: "位置合格",
  angleValid: "角度合格",
  valid: "全部检测合格",
  areaRatio: "画面占比",
  centerOffset: "中心偏移",
  confidence: "检测置信度",
  movement: "移动幅度",
  widthRatio: "宽度比例",
  heightRatio: "高度比例",
  angle: "倾斜角度",
  perspectiveSkew: "透视变形",
  fresh: "当前帧检测成功",
  missedFrames: "连续丢失帧数",
  stableFrames: "连续稳定帧数",
  stableProgress: "稳定进度",
  detectorFps: "实际检测帧率",
  detectorTimeMs: "单帧检测耗时毫秒",
  recordingElapsedMs: "已采集时长毫秒",
  minimumRecordingMs: "最短采集时长毫秒",
  sourceWidth: "原始画面宽度",
  sourceHeight: "原始画面高度",
  secureContext: "安全上下文",
  hasGetUserMedia: "支持摄像头 API",
  hasMediaRecorder: "支持录制 API",
  camera: "摄像头名称",
  width: "画面宽度",
  height: "画面高度",
  frameRate: "帧率",
};

const VALUE_LABELS = {
  front: "正面",
  back: "反面",
  automatic: "自动",
  manual: "手动",
  initial: "初始化",
  requesting: "请求权限中",
  scanning: "检测中",
  document_invalid: "证件位置不合格",
  document_ready: "证件检测合格",
  processing: "处理中",
  error: "错误",
  "stable-document": "稳定后自动拍摄",
  "document-positioned": "证件完整且居中",
  "manual-capture": "手动点击后短录制",
  button: "手动点击拍摄",
  SEARCHING: "正在寻找证件",
  SHOW_ENTIRE_DOCUMENT: "请显示完整证件",
  MOVE_CLOSER: "请靠近一些",
  MOVE_AWAY: "请远离一些",
  CENTER_DOCUMENT: "请将证件居中",
  HOLD_DOCUMENT_FLAT: "请将证件放平",
  HOLD_STEADY: "请保持稳定",
  GOOD: "检测通过",
  RECORDING: "正在采集",
  PROCESSING: "正在处理",
};

const translateDetails = (details) =>
  Object.fromEntries(
    Object.entries(details).map(([key, value]) => [
      DETAIL_LABELS[key] || key,
      typeof value === "string" ? VALUE_LABELS[value] || value : value,
    ]),
  );

export const logDocumentCamera = (event, details) => {
  if (!DEBUG_ENABLED) return;

  const translatedEvent = EVENT_LABELS[event] || event;

  if (details === undefined) {
    console.info(`${PREFIX} ${translatedEvent}`);
    return;
  }

  // console.info(`${PREFIX} ${translatedEvent}`, translateDetails(details));
};

export const warnDocumentCamera = (event, error) => {
  if (!DEBUG_ENABLED) return;

  console.warn(`${PREFIX} ${EVENT_LABELS[event] || event}`, {
    错误类型: error?.name || "Error",
    错误信息: error?.message || String(error || "未知错误"),
  });
};
